import React, { createContext, useContext, useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import * as api from '../api';

const AuthContext = createContext(null);

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper to load profile and store session data
  const hydrateSession = async (supabaseUser, accessToken, refreshToken) => {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('user', JSON.stringify(supabaseUser));
    setUser(supabaseUser);
    try {
      const profileData = await api.getUser(supabaseUser.id);
      setProfile(profileData.user);
    } catch (_) {}
  };

  useEffect(() => {
    // Handle initial session (email/password or page reload)
    const token = localStorage.getItem('access_token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      const u = JSON.parse(savedUser);
      setUser(u);
      api.getUser(u.id)
        .then(data => setProfile(data.user))
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }

    // Listen for OAuth callback — fires when Supabase redirects back after Google login
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session) {
        await hydrateSession(session.user, session.access_token, session.refresh_token);

        // For Google OAuth users, ensure a users row exists in our DB
        if (session.user.app_metadata?.provider === 'google') {
          try {
            await api.googleSync(session.access_token);
          } catch (_) {}
        }

        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email, password) => {
    const data = await api.login(email, password);
    await hydrateSession(data.user, data.access_token, data.refresh_token);
    return data;
  };

  // Triggers Google OAuth redirect — Supabase handles the rest via onAuthStateChange
  const loginWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw new Error(error.message);
  };

  const logout = () => {
    localStorage.clear();
    supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  };

  const refreshProfile = async () => {
    if (user) {
      const data = await api.getUser(user.id);
      setProfile(data.user);
    }
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, login, loginWithGoogle, logout, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);